import { Button } from '@synoptic/ui-kit/button/button.tsx';
import { Input } from '@synoptic/ui-kit/input/input.tsx';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.tsx';
import { autoSignIn, confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';
import { FC, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  formFieldsContainer,
  formHeading,
  subButton,
  submitAuthFormButton,
} from '../_auth/page.css.ts';
import { cardBody } from '@/routes/_auth/page.css';

type FormInputs = {
  confirmationCode: string;
};

export const ConfirmEmailForm: FC<{
  onConfirmEmail: () => void;
  username: string;
  email: string;
}> = ({ username, onConfirmEmail, email }) => {
  const { handleSubmit, formState, reset, register } = useForm<FormInputs>();
  const formRef = useRef<HTMLFormElement>(null);
  const toast = useToast();
  const [isResending, setIsResending] = useState(false);

  const handleResendEmail = async () => {
    try {
      setIsResending(true);
      await resendSignUpCode({ username });
    } catch (e) {
      toast.error({ title: String(e) });
    } finally {
      setIsResending(false);
    }
  };

  const submit: SubmitHandler<FormInputs> = async (data) => {
    const { confirmationCode } = data;
    try {
      await confirmSignUp({ username, confirmationCode });
      await autoSignIn();

      onConfirmEmail();
      reset();
    } catch (e) {
      toast.error({ title: String(e) });
    }
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit(submit)}>
      <h1 className={formHeading}>Verify your email</h1>
      <p className={cardBody}>
        We’ve just sent a 6-digit code to {email}. Please check your inbox and
        enter the code below:
      </p>
      <div className={formFieldsContainer}>
        <Input
          {...register('confirmationCode', {
            required: true,
          })}
          placeholder={'Enter verification code'}
          autoComplete={'off'}
          label={'Verification code'}
          autoFocus
        />
        <div>
          <Button
            loading={isResending}
            disabled={formState.isSubmitting}
            onClick={handleResendEmail}
            size={'small'}
            variant="ghost"
            className={subButton}
          >
            Resend email
          </Button>
        </div>
        <Button
          className={submitAuthFormButton}
          size={'large'}
          type="submit"
          loading={formState.isSubmitting}
        >
          Verify email
        </Button>
      </div>
    </form>
  );
};
